import React from "react";
import { useTheme } from "@mui/material/styles";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Badge,
  IconButton,
  Paper,
  Tabs,
  Tab,
  InputBase,
  Dialog,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceIcon from "@mui/icons-material/Place";
import PeopleIcon from "@mui/icons-material/People";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import LockIcon from "@mui/icons-material/Lock";
import {
  fetchClassById,
  fetchSessionsForCourse,
  fetchClassLevelContent,
  fetchChecklistsForCourse,
  fetchChecklistsForSession,
  fetchTestsForCourse,
  fetchTestsForSession,
  fetchChecklistEvaluation,
  fetchLatestTestSubmission,
  fetchUsersByIds,
  downloadContentFile,
  getContentAccessUrl,
  fetchStudentAttendance,
  fetchSessionContent,
  fetchMySessionAttendance,
  studentCheckIn,
  studentCheckOut,
  fetchPracticeAttempts,
  createPracticeAttempt,
  fetchMyLearningPlan,
  fetchMyContentLink,
} from "../api/backend";
import VirtualLearningDocumentViewer from "./courseHub/VirtualLearningDocumentViewer";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  fetchCourseRoster,
  createDmThread,
  fetchClassAnnouncementPreview,
  fetchMessagingThreads,
  fetchCourseSkillSheetAssignments,
} from "../api/apiClient";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useTenant } from "../contexts/TenantContext";
import SessionCalendarView from "../components/dashboard/SessionCalendarView";
import MessagingDrawer, {
  filterThreadsForClass,
} from "../components/dashboard/MessagingDrawer";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import { T as SCT, SectionGroup, groupItemsIntoSections } from "../components/checklistScoring";
import PracticeHistoryPanel from "../components/PracticeHistoryPanel";
import ChecklistAssessmentScreen from "../components/ChecklistAssessmentScreen";
import ClassOverview from "../components/classOverview/ClassOverview";
import StudentSkillCard from "../components/StudentSkillCard";
import {
  sessionDisplayTitle,
  parseSessionTopics,
  materialsForSession,
  sessionStatusBadge,
  formatSessionTimeRange,
} from "./studentSessionHelpers";
import { countActive } from "./contentIsActiveHelpers";

const T = {
  ink: "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  pageBg: "var(--color-surface-2)",
  primary: "var(--color-primary)",
  p700: "var(--color-primary)",
  p50: "color-mix(in srgb, var(--color-primary) 6%, transparent)",
  p10: "color-mix(in srgb, var(--color-primary) 6%, transparent)",
  blue: "var(--color-primary)",
  blue50: "rgba(37,99,235,.10)",
  green: "var(--color-success)",
  green50: "var(--color-success-bg)",
  teal: "var(--color-accent-violet)",
  teal50: "rgba(14,124,123,.10)",
  amber: "var(--color-warn)",
  amber50: "var(--color-warn-bg)",
  live: "#c23d68",
  liveBg: "color-mix(in srgb, var(--color-accent-pink) 15%, transparent)",
  purple: "var(--color-status-neutral)",
  purple50: "rgba(121,98,140,.14)",
  danger: "var(--color-danger)",
  danger50: "var(--color-danger-bg)",
};

const DS = {
  border: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-2)",
};

const TABS = [
  { key: "sessions", label: "Sessions", icon: CalendarMonthIcon },
  { key: "documents", label: "Documents", icon: InsertDriveFileOutlinedIcon },
  {
    key: "skills",
    label: "Skill Check Sheets",
    icon: AssignmentTurnedInOutlinedIcon,
  },
  { key: "tests", label: "Tests", icon: AssignmentOutlinedIcon },
  { key: "virtual-learning", label: "Virtual Learning", icon: AssignmentTurnedInOutlinedIcon },
  { key: "reports", label: "Reports", icon: BarChartIcon },
  { key: "roster", label: "Roster", icon: PeopleIcon },
];
export const TAB_ROUTES = [
  "",
  "documents",
  "skill-sheets",
  "tests",
  "virtual-learning",
  "reports",
  "roster",
];

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelativeTime(isoStr) {
  if (!isoStr) return null;
  const then = new Date(isoStr).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Date.now() - then;
  const diffMin = Math.max(0, Math.round(diffMs / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(isoStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function truncateText(text, max) {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

export function getPinnedAnnouncementMessage(preview) {
  return preview?.pinnedMessage || null;
}

export function rosterRoleLabel(rosterEntry) {
  return rosterEntry?.role === "INSTRUCTOR" ? "Instructor" : null;
}

function getSessionStatus(session) {
  if (!session.sessionDate) return "upcoming";
  const today = new Date().toISOString().slice(0, 10);
  if (session.sessionDate > today) return "upcoming";
  if (session.sessionDate === today) return "today";
  return "completed";
}

export function getDocKind(item) {
  const ct = (item.contentType || "").toUpperCase();
  if (ct === "LINK" || ct === "EXTERNAL_LINK") return "link";
  if (ct === "VIDEO") return "video";
  const name = (item.fileName || "").toLowerCase();
  if (name.endsWith(".mp4") || name.endsWith(".mov") || name.endsWith(".avi") || name.endsWith(".webm"))
    return "video";
  if (name.endsWith(".ppt") || name.endsWith(".pptx")) return "ppt";
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(name)) return "image";
  return "doc";
}

export function getDocExt(item) {
  const ct = (item.contentType || "").toUpperCase();
  if (ct === "LINK" || ct === "EXTERNAL_LINK") return "URL";
  const name = (item.fileName || "").toLowerCase();
  const dot = name.lastIndexOf(".");
  if (dot !== -1)
    return name
      .slice(dot + 1)
      .toUpperCase()
      .slice(0, 4);
  return "FILE";
}

export function sessionLabel(sessions, sessionId) {
  if (sessionId === "classwide") return "Class-wide";
  const s = sessions.find((s) => String(s.id) === sessionId);
  if (!s) return `Session ${sessionId}`;
  if (s.name) return s.name;
  if (s.topic) return s.topic;
  if (s.sessionNumber != null) return `Session ${s.sessionNumber}`;
  return `Session ${sessions.indexOf(s) + 1}`;
}

export function getLocalCalendarDate(date = new Date()) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function isValidSessionDate(sessionDate) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(sessionDate || "")) return false;
  const [year, month, day] = sessionDate.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  return (
    parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
  );
}

export function isStudentVisibleSession(
  session,
  today = getLocalCalendarDate(),
) {
  return (
    session?.enabled === true ||
    (isValidSessionDate(session?.sessionDate) && session.sessionDate <= today)
  );
}

export function filterStudentVisibleSessions(
  sessions,
  today = getLocalCalendarDate(),
) {
  return (sessions || []).filter((session) =>
    isStudentVisibleSession(session, today),
  );
}

export function filterStudentVisibleSessionContent(
  items,
  sessions,
  today = getLocalCalendarDate(),
) {
  return (items || []).filter((item) => {
    if (item.sessionId == null) return true;
    const session = sessions.find(
      (candidate) => String(candidate.id) === String(item.sessionId),
    );
    return isStudentVisibleSession(session, today);
  });
}

/**
 * Tags each session-scoped item with `_sessionLocked` instead of removing it,
 * so locked content still renders (visibly locked) rather than disappearing.
 */
export function tagSessionLock(items, sessions, today = getLocalCalendarDate()) {
  return (items || []).map((item) => {
    if (item.sessionId == null) return { ...item, _sessionLocked: false };
    const session = sessions.find(
      (candidate) => String(candidate.id) === String(item.sessionId),
    );
    return { ...item, _sessionLocked: !isStudentVisibleSession(session, today) };
  });
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

function pct(passed, total) {
  return total > 0 ? Math.round((passed / total) * 100) : null; // null = "not applicable", not 0%
}

// TestSubmission API responses carry score/totalPoints/passed, not gradePercent
// (gradePercent only exists on ChecklistEvaluation) — derive the score % client-side,
// matching the pattern already used in TestResultsPage.js.
export function testScorePct(submission) {
  if (
    !submission ||
    submission.totalPoints == null ||
    submission.totalPoints <= 0
  )
    return null;
  return Math.round((submission.score / submission.totalPoints) * 100);
}

// Recalculate passed from score — backend may have stored a stale/incorrect value
// (e.g. instructor edited test.passingScore after the student submitted). Mirrors
// TestResultsPage.js's recompute so the two views never disagree for the same submission.
// passingScore of 0 is treated as "not configured" (falls back to 70%), matching
// the backend's own determinePassed() — a literal 0% threshold would be trivially
// met by a score of 0, auto-passing a student who answered nothing correctly.
export function testPassedLive(submission, test) {
  if (!submission) return false;
  const totalPoints = submission.totalPoints ?? 0;
  const scorePct = testScorePct(submission);
  const passingPct = test?.passingScore > 0 ? test.passingScore : 70;
  return totalPoints > 0 ? scorePct >= passingPct : submission.passed === true;
}

function Btn({
  variant = "outline",
  size = "md",
  onClick,
  disabled,
  children,
  sx = {},
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontFamily: "inherit",
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: "10px",
    border: "1px solid transparent",
    transition: "all .15s",
    fontWeight: 600,
    opacity: disabled ? 0.45 : 1,
    fontSize: size === "sm" ? 12.5 : 13.5,
    height: size === "sm" ? 32 : 38,
    px: size === "sm" ? "12px" : "16px",
  };
  const variants = {
    primary: {
      bgcolor: tPrimary,
      color: "var(--color-on-primary)",
      borderColor: tPrimary,
      "&:hover": disabled ? {} : { bgcolor: T.p700 },
    },
    outline: {
      bgcolor: T.surface,
      color: T.ink,
      borderColor: T.line,
      "&:hover": disabled ? {} : { bgcolor: T.surface2 },
    },
    ghost: {
      bgcolor: "transparent",
      color: T.ink2,
      "&:hover": disabled ? {} : { bgcolor: T.surface3, color: T.ink },
    },
  };
  return (
    <Box
      component="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{ ...base, ...variants[variant], ...sx }}
    >
      {children}
    </Box>
  );
}

/** Student roster row grid — Student | Role | Actions (no performance stats). */
const STUDENT_ROSTER_GRID = "minmax(180px, 1.6fr) minmax(90px, 0.7fr) auto";

export function RosterMemberCard({ student, tPrimary, onMessage }) {
  const role = rosterRoleLabel(student);
  const email = student.email || "";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr auto",
          sm: STUDENT_ROSTER_GRID,
        },
        gap: { xs: "10px 12px", sm: "14px" },
        alignItems: "center",
        px: "18px",
        py: "14px",
        borderBottom: `1px solid ${T.line2}`,
        bgcolor: "#ffffff",
        transition: "background .12s",
        "&:hover": { bgcolor: T.surface2 },
        "&:last-child": { borderBottom: 0 },
      }}
    >
      {/* Student */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "36px 1fr",
          gap: "12px",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            bgcolor: tPrimary,
            color: "var(--color-on-primary)",
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "-.01em",
            flexShrink: 0,
          }}
        >
          {initials(student.name)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: T.ink,
              mb: "2px",
              letterSpacing: "-.005em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {student.name}
          </Typography>
          {email ? (
            <Typography
              sx={{
                fontSize: "11.5px",
                color: T.ink3,
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {email}
            </Typography>
          ) : null}
          {/* Role under name on narrow screens only — single source when no Role col */}
          {role && (
            <Typography
              className="student-roster-role-mobile"
              sx={{
                display: { xs: "block", sm: "none" },
                mt: "2px",
                fontSize: 12,
                fontWeight: 600,
                color: T.ink4,
              }}
            >
              {role}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Role column — desktop/tablet */}
      <Box
        className="student-roster-role-col"
        sx={{ display: { xs: "none", sm: "block" } }}
      >
        {role ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              height: 24,
              px: "10px",
              borderRadius: "999px",
              bgcolor: T.surface3,
              border: `1px solid ${T.line}`,
              color: T.ink2,
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: ".02em",
              whiteSpace: "nowrap",
            }}
          >
            {role}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: T.ink3, fontWeight: 500 }}>
            Student
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
        <Btn variant="primary" size="sm" onClick={() => onMessage(student)}>
          Message
        </Btn>
      </Box>
    </Box>
  );
}

function requestGps() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 8000 },
    );
  });
}

function formatAttendanceTime(isoStr) {
  if (!isoStr) return "—";
  // Backend sends naive LocalDateTime (server clock is forced to UTC) with no
  // zone suffix — mark it UTC explicitly so the browser converts to local time
  // instead of treating the string as already-local.
  const utcStr = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoStr) ? isoStr : `${isoStr}Z`;
  return new Date(utcStr).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Session row (expand/collapse — mock: topics, sign-in, materials) ───────────
function SessionRow({
  session,
  idx,
  isLast,
  status,
  mon,
  day,
  tPrimary,
  materials,
  onPractice,
  classId,
  firstSessionId,
  navigate,
  testSubmissions = {},
}) {
  const [open, setOpen] = React.useState(false);
  const [attendance, setAttendance] = React.useState(null);
  const [loadingAttendance, setLoadingAttendance] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState(false);

  const isLive = status === "today";
  const statusProps = sessionStatusBadge(status);
  const topics = parseSessionTopics(session.topics ?? session.topic);
  const title = sessionDisplayTitle(session, idx);
  const timeLabel = formatSessionTimeRange(session);

  React.useEffect(() => {
    if (!open || attendance !== null) return;
    setLoadingAttendance(true);
    fetchMySessionAttendance(session.id)
      .then((res) => setAttendance(res || {}))
      .catch(() => setAttendance({}))
      .finally(() => setLoadingAttendance(false));
  }, [open, session.id, attendance]);

  const isCheckedIn = attendance?.attended && !attendance?.checkedOutAt;
  const isCheckedOut = !!attendance?.checkedOutAt;

  async function handleCheckIn(e) {
    e.stopPropagation();
    setActionLoading(true);
    try {
      const coords = await requestGps();
      const result = await studentCheckIn(session.id, coords);
      setAttendance(result);
    } catch {
      /* silent */
    } finally {
      setActionLoading(false);
    }
  }

  async function handleCheckOut(e) {
    e.stopPropagation();
    setActionLoading(true);
    try {
      const coords = await requestGps();
      const result = await studentCheckOut(session.id, coords);
      setAttendance(result);
    } catch {
      /* silent */
    } finally {
      setActionLoading(false);
    }
  }

  const sectionLabelSx = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".1em",
    textTransform: "uppercase",
    color: T.ink3,
    mb: "10px",
    fontFamily: "monospace",
  };

  return (
    <Box sx={{ borderBottom: !isLast ? `1px solid ${T.line2}` : "none" }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: "10px", sm: "14px" },
          p: "14px 20px",
          cursor: "pointer",
          transition: "bgcolor .12s",
          bgcolor: open ? T.surface2 : "transparent",
          "&:hover": { bgcolor: T.surface2 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 44,
              flexShrink: 0,
            }}
          >
            {mon && (
              <Box sx={{ fontSize: 9, fontWeight: 700, letterSpacing: ".08em", color: T.ink3 }}>
                {mon}
              </Box>
            )}
            <Box
              sx={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-.03em",
                lineHeight: 1,
                color: status === "completed" ? T.ink3 : tPrimary,
              }}
            >
              {day ?? (session.sessionNumber || idx + 1)}
            </Box>
          </Box>

          <Box sx={{ width: "1px", height: 34, bgcolor: T.line2, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 14, fontWeight: 700, color: T.ink, mb: "2px" }}>{title}</Box>
            <Box
              sx={{
                fontSize: 12.5,
                color: T.ink3,
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <AccessTimeIcon sx={{ fontSize: 12, color: T.ink4 }} />
                {timeLabel}
              </Box>
              {open && session.location && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <PlaceIcon sx={{ fontSize: 12, color: T.ink4 }} />
                  {session.location}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "space-between", sm: "flex-end" },
            gap: "10px",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: 11,
              fontWeight: 700,
              px: "9px",
              py: "3px",
              borderRadius: "999px",
              bgcolor: statusProps.bg,
              border: `1px solid ${statusProps.border}`,
              color: statusProps.color,
              whiteSpace: "nowrap",
              flexShrink: 0,
              letterSpacing: ".02em",
              "&::before": {
                content: '""',
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: statusProps.color,
                flexShrink: 0,
              },
            }}
          >
            {statusProps.label}
          </Box>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              color: T.ink3,
              transition: "transform .2s",
              transform: open ? "rotate(180deg)" : "none",
              flexShrink: 0,
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>

      {open && (
        <Box
          sx={{ px: "20px", pb: "20px", pt: "4px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            {/* TOPICS COVERED */}
            <Box>
              <Typography sx={sectionLabelSx}>Topics covered</Typography>
              {topics.length === 0 ? (
                <Typography sx={{ fontSize: 13, fontStyle: "italic", color: T.ink4 }}>
                  No topics listed yet.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: "18px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {topics.map((topic) => (
                    <Box
                      component="li"
                      key={topic}
                      sx={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.4 }}
                    >
                      {topic}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* SIGN IN / SIGN OUT */}
            <Box>
              <Typography sx={sectionLabelSx}>Sign in / Sign out</Typography>
              <Box
                sx={{
                  bgcolor: "#ffffff",
                  border: `1px solid ${T.line2}`,
                  borderRadius: "12px",
                  p: "14px 16px",
                }}
              >
                {loadingAttendance ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: T.ink3, fontSize: 13 }}>
                    <CircularProgress size={16} sx={{ color: tPrimary }} />
                    Loading attendance…
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: "12px",
                        mb: isLive ? "14px" : 0,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3, mb: "6px" }}>
                          Sign in · Session start
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            fontWeight: 600,
                            color: attendance?.checkedInAt ? T.ink : T.ink4,
                            mb: isLive && !isCheckedIn && !isCheckedOut ? "8px" : 0,
                          }}
                        >
                          {formatAttendanceTime(attendance?.checkedInAt)}
                        </Typography>
                        {isLive && !isCheckedIn && !isCheckedOut && (
                          <Btn
                            variant="primary"
                            onClick={handleCheckIn}
                            disabled={actionLoading}
                            sx={{ width: "100%", textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 800 }}
                          >
                            {actionLoading ? "Signing in…" : "Sign in to session"}
                          </Btn>
                        )}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.ink3, mb: "6px" }}>
                          Sign out · Session end
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            fontWeight: 600,
                            color: attendance?.checkedOutAt ? T.ink : T.ink4,
                            mb: isLive && isCheckedIn && !isCheckedOut ? "8px" : 0,
                          }}
                        >
                          {formatAttendanceTime(attendance?.checkedOutAt)}
                        </Typography>
                        {isLive && isCheckedIn && !isCheckedOut ? (
                          <Btn
                            variant="outline"
                            onClick={handleCheckOut}
                            disabled={actionLoading}
                            sx={{
                              width: "100%",
                              color: T.danger,
                              borderColor: "color-mix(in srgb, var(--color-danger) 25%, transparent)",
                            }}
                          >
                            <LogoutIcon sx={{ fontSize: 15 }} />
                            {actionLoading ? "Signing out…" : "Sign out of session"}
                          </Btn>
                        ) : (
                          <Btn
                            variant="outline"
                            disabled
                            sx={{ width: "100%", opacity: 0.45 }}
                          >
                            Sign out of session
                          </Btn>
                        )}
                      </Box>
                    </Box>
                    {!isLive && !isCheckedIn && !isCheckedOut && (
                      <Typography sx={{ fontSize: 12.5, color: T.ink3, mt: "4px" }}>
                        {status === "upcoming"
                          ? "Sign-in opens on the day of this session."
                          : "No attendance recorded for this session."}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* SESSION MATERIALS */}
            <Box>
              <Typography sx={sectionLabelSx}>Session materials</Typography>
              {!materials.unlocked ? (
                <Typography sx={{ fontSize: 13, color: T.ink3 }}>
                  Documents unlock once this session opens.
                </Typography>
              ) : materials.isEmpty ? (
                <Typography sx={{ fontSize: 13, fontStyle: "italic", color: T.ink4 }}>
                  No materials assigned to this session yet.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {materials.docs.map((item) => (
                    <DocCard key={`doc-${item.id}`} item={item} />
                  ))}
                  {materials.checklists.map((cl) => (
                    <Box
                      key={`cl-${cl.id}`}
                      sx={{
                        bgcolor: "#ffffff",
                        border: `1px solid ${T.line}`,
                        borderRadius: "12px",
                        p: "14px 16px",
                        display: "grid",
                        gridTemplateColumns: { xs: "42px 1fr", sm: "42px 1fr auto" },
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "10px",
                          bgcolor: T.teal50,
                          color: T.teal,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
                          {cl.title || "Skill check sheet"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                          {(cl.items || []).length || cl.itemCount || 0} skills
                          {cl.description ? ` · ${cl.description}` : ""}
                        </Typography>
                      </Box>
                      <Btn
                        variant="primary"
                        size="sm"
                        onClick={() => onPractice?.(cl)}
                        sx={{ justifySelf: { xs: "stretch", sm: "end" }, gridColumn: { xs: "1 / -1", sm: "auto" } }}
                      >
                        <PeopleAltIcon sx={{ fontSize: 15 }} /> Preview &amp; Practice
                      </Btn>
                    </Box>
                  ))}
                  {materials.tests.map((test) => {
                    const submission = testSubmissions[test.id];
                    const canTake =
                      submission?.canRetake != null
                        ? submission.canRetake
                        : !submission?.latestSubmission;
                    return (
                      <Box
                        key={`test-${test.id}`}
                        sx={{
                          bgcolor: "#ffffff",
                          border: `1px solid ${T.line}`,
                          borderRadius: "12px",
                          p: "14px 16px",
                          display: "grid",
                          gridTemplateColumns: { xs: "42px 1fr", sm: "42px 1fr auto" },
                          gap: "12px",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "10px",
                            bgcolor: T.purple50,
                            color: T.purple,
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          <AssignmentOutlinedIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
                            {test.title || "Test"}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: T.ink3 }}>
                            {[
                              test.timeLimitMinutes != null ? `${test.timeLimitMinutes} min` : null,
                              test.questionCount != null ? `${test.questionCount} questions` : null,
                              test.passingScore != null ? "Pass to advance" : null,
                            ]
                              .filter(Boolean)
                              .join(" · ") || "Written test"}
                          </Typography>
                        </Box>
                        <Btn
                          variant="primary"
                          size="sm"
                          disabled={!canTake && !submission?.latestSubmission}
                          onClick={() => {
                            if (!navigate || !classId) return;
                            const path = submission?.latestSubmission && !canTake
                              ? (test.sessionId ? `/classes/${classId}/session/${test.sessionId}/test/${test.id}/results` : `/classes/${classId}/tests/${test.id}/results`)
                              : (test.sessionId ? `/classes/${classId}/session/${test.sessionId}/test/${test.id}` : `/classes/${classId}/tests/${test.id}`);
                            navigate(path);
                          }}
                          sx={{ justifySelf: { xs: "stretch", sm: "end" }, gridColumn: { xs: "1 / -1", sm: "auto" } }}
                        >
                          {submission?.latestSubmission && !canTake ? "View results" : "Start test"}
                        </Btn>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Lock overlay: wraps content that's inaccessible until its session opens ────
function LockOverlay({ locked, children }) {
  if (!locked) return children;
  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ opacity: 0.45, pointerEvents: "none", filter: "grayscale(35%)" }}>
        {children}
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          borderRadius: "14px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            bgcolor: "var(--color-surface-canvas-light)",
            border: `1px solid ${T.line}`,
            borderRadius: "999px",
            px: "12px",
            py: "6px",
            boxShadow: "var(--shadow-card-sm)",
          }}
        >
          <LockIcon sx={{ fontSize: 15, color: T.ink4 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink3 }}>
            Locked until session opens
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Document card ──────────────────────────────────────────────────────────────
export function DocCard({ item }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const kind = getDocKind(item);
  const ext = getDocExt(item);
  const [downloading, setDownloading] = React.useState(false);
  const [viewerUrl, setViewerUrl] = React.useState(null);
  const [viewerKind, setViewerKind] = React.useState(null);

  const iconBg =
    kind === "video" ? T.danger50 : kind === "link" ? T.blue50 : T.p10;
  const iconColor =
    kind === "video" ? T.danger : kind === "link" ? T.blue : tPrimary;
  const Icon =
    kind === "video"
      ? PlayCircleOutlineIcon
      : kind === "link"
        ? LinkOutlinedIcon
        : InsertDriveFileOutlinedIcon;
  const isPerStudentLink =
    item.contentType === "EXTERNAL_LINK" && item.linkScope === "PER_STUDENT";
  const isLink = !isPerStudentLink && (kind === "link" || (!!item.url && !item.hasFile));
  const isInlineViewable = kind === "video" || kind === "ppt" || kind === "image";
  const linkUnavailable = isPerStudentLink && !item.hasMyLink;

  function handleClick(e) {
    if (isPerStudentLink) {
      e.preventDefault();
      if (downloading || linkUnavailable) return;
      setDownloading(true);
      fetchMyContentLink(item.id)
        .then((data) => {
          if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
        })
        .catch(console.error)
        .finally(() => setDownloading(false));
      return;
    }
    if (isLink) return;
    e.preventDefault();
    if (downloading) return;
    if (isInlineViewable) {
      setDownloading(true);
      (kind === "image" ? downloadContentFile(item.id) : getContentAccessUrl(item.id))
        .then((url) => {
          setViewerKind(kind);
          setViewerUrl(url);
        })
        .catch(console.error)
        .finally(() => setDownloading(false));
      return;
    }
    setDownloading(true);
    downloadContentFile(item.id)
      .then((blobUrl) => window.open(blobUrl, "_blank"))
      .catch(console.error)
      .finally(() => setDownloading(false));
  }

  return (
    <>
    <Box
      component={isLink ? "a" : "div"}
      href={isLink ? item.url || "#" : undefined}
      target={isLink ? "_blank" : undefined}
      rel={isLink ? "noopener noreferrer" : undefined}
      onClick={handleClick}
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "14px",
        p: "20px 22px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px",
        transition: "border-color .15s, box-shadow .15s, transform .15s",
        cursor: linkUnavailable ? "default" : downloading ? "wait" : "pointer",
        textDecoration: "none",
        color: "inherit",
        opacity: linkUnavailable ? 0.6 : downloading ? 0.7 : 1,
        "&:hover": linkUnavailable
          ? undefined
          : {
              borderColor: "var(--color-hairline-cool)",
              boxShadow: "var(--shadow-card-sm)",
              transform: "translateY(-1px)",
            },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "12px",
            bgcolor: iconBg,
            color: iconColor,
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>
        <Box sx={{ color: T.ink4 }}>
          {isLink ? (
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, width: "100%" }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: T.ink,
            mb: "6px",
          }}
        >
          {item.title}
        </Typography>
        {isPerStudentLink && (
          <Typography sx={{ fontSize: 12, color: linkUnavailable ? T.ink4 : T.blue, fontWeight: 600, mb: "6px" }}>
            {linkUnavailable ? "Not yet available" : "Open my link"}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            fontSize: 12.5,
            color: T.ink3,
            fontWeight: 500,
          }}
        >
          <Box
            sx={{
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontSize: 10.5,
              bgcolor: T.surface3,
              px: "7px",
              py: "3px",
              borderRadius: "4px",
              color: T.ink2,
              fontWeight: 700,
              alignSelf: "flex-start",
            }}
          >
            {ext}
          </Box>
          {item.description && <span>{item.description}</span>}
        </Box>
      </Box>
    </Box>
    <Dialog
      open={!!viewerUrl}
      onClose={() => { setViewerUrl(null); setViewerKind(null); }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "85vh" } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "10px 16px", borderBottom: `1px solid ${T.line}` }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{item.title}</Typography>
        <IconButton size="small" onClick={() => { setViewerUrl(null); setViewerKind(null); }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, bgcolor: "#000" }}>
        {viewerKind === "video" && viewerUrl && (
          <video src={viewerUrl} controls autoPlay style={{ width: "100%", height: "100%" }} />
        )}
        {viewerKind === "image" && viewerUrl && (
          <img src={viewerUrl} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        )}
        {viewerKind === "ppt" && viewerUrl && (
          <iframe
            title={item.title}
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(viewerUrl)}`}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        )}
      </Box>
    </Dialog>
    </>
  );
}

// ── Skill sheet card ───────────────────────────────────────────────────────────
// ── Peer practice: skill sheet preview ──────────────────────────────────────────
function SkillSheetPreview({ checklist, myAttempts, onBack, onStartPeerEval, onStartSolo }) {
  const items = React.useMemo(() => checklist.items || [], [checklist.items]);
  const sections = React.useMemo(() => groupItemsIntoSections(items), [items]);
  const emptyScores = {};

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Box
        component="button"
        onClick={onBack}
        sx={{ display: "inline-flex", alignItems: "center", gap: "8px", color: SCT.primary, fontWeight: 600, fontSize: 13.5, bgcolor: "transparent", border: "none", cursor: "pointer", p: "6px 4px", mb: "10px" }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to session
      </Box>

      <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${SCT.border}`, borderRadius: "18px", p: "24px 28px", mb: "18px", display: "flex", gap: "18px", alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "var(--color-warn-bg)", color: "var(--color-warn)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <AccessTimeIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--color-warn)", mb: "6px" }}>
            Assigned to this session
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.02em", color: SCT.ink, mb: "6px" }}>{checklist.title}</Typography>
          {checklist.description && <Typography sx={{ fontSize: 14, color: "rgba(31,22,51,0.7)", mb: "10px" }}>{checklist.description}</Typography>}
          <Box sx={{ display: "flex", gap: "18px", flexWrap: "wrap", fontSize: 13, color: "rgba(31,22,51,0.7)" }}>
            <span><strong>{items.length}</strong> skills</span>
            {checklist.passingScore != null && <span><strong>{checklist.passingScore}</strong> pts to pass</span>}
            {checklist.timeLimitMin != null && <span><strong>{checklist.timeLimitMin}</strong> min limit</span>}
          </Box>
        </Box>
        <Box
          component="button"
          data-testid="practice-with-classmate"
          onClick={onStartPeerEval}
          sx={{ display: "inline-flex", alignItems: "center", gap: "8px", height: 42, px: "18px", borderRadius: "11px", border: "none", bgcolor: SCT.primary, color: "var(--color-on-primary)", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer", "&:hover": { bgcolor: "var(--color-primary)" } }}
        >
          <PeopleAltIcon sx={{ fontSize: 17 }} /> Practice with classmate
        </Box>
        <Box
          component="button"
          data-testid="practice-solo"
          onClick={onStartSolo}
          sx={{ display: "inline-flex", alignItems: "center", height: 42, px: "18px", borderRadius: "11px", border: `1.5px solid ${SCT.primary}`, bgcolor: "transparent", color: SCT.primary, fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
        >
          Practice solo
        </Box>
      </Box>

      <Box sx={{ bgcolor: "color-mix(in srgb, var(--color-primary) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--color-primary) 12%, transparent)", borderRadius: "12px", p: "13px 15px", display: "flex", gap: "10px", mb: "18px", fontSize: 13, color: "rgba(31,22,51,0.7)", lineHeight: 1.55 }}>
        Practice as many times as you'd like, on your own or with an enrolled classmate, before the official evaluation. Your instructor always does the official evaluation. Every practice attempt — scores and notes — is saved here and shown to your instructor.
        {checklist.peerAssessmentRequired !== false && (
          <Box component="span" data-testid="peer-practice-required" sx={{ display: "block", fontWeight: 700, mt: "6px" }}>
            Peer practice is required: you need a passing practice with a classmate before your instructor can sign you off.
          </Box>
        )}
      </Box>

      <Typography sx={{ fontSize: 15, fontWeight: 700, color: SCT.ink, mb: "12px" }}>Checklist <Box component="span" sx={{ fontWeight: 500, color: SCT.na, fontSize: 12.5 }}>(preview only)</Box></Typography>
      {sections.map((sec, i) => (
        <SectionGroup key={sec.title + i} section={sec} sectionIdx={i} scores={emptyScores} notes={{}} onScore={() => {}} onNote={() => {}} locked />
      ))}

      <PracticeHistoryPanel candidateName="You" attempts={myAttempts || []} items={items} passingScore={checklist.passingScore} />
    </Box>
  );
}

// ── Peer practice: peer picker ──────────────────────────────────────────────────
export function PeerPicker({ roster, selfId, onBack, onConfirm }) {
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const classmates = roster.filter((r) => String(r.id) !== String(selfId));
  const peers = classmates.filter((r) => r.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      <Box
        component="button"
        onClick={onBack}
        sx={{ display: "inline-flex", alignItems: "center", gap: "8px", color: SCT.primary, fontWeight: 600, fontSize: 13.5, bgcolor: "transparent", border: "none", cursor: "pointer", p: "6px 4px", mb: "10px" }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to skill sheet
      </Box>

      <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${SCT.border}`, borderRadius: "16px", p: "22px 26px", mb: "18px" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--color-warn)", mb: "6px" }}>
          Practice peer evaluation
        </Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.02em", color: SCT.ink, mb: "6px" }}>Who's conducting this evaluation?</Typography>
        <Typography sx={{ fontSize: 14, color: "rgba(31,22,51,0.7)" }}>Pick the classmate who will score you. Hand them your device to run the checklist.</Typography>
      </Box>

      <Box sx={{ position: "relative", mb: "14px" }}>
        <SearchIcon sx={{ position: "absolute", left: "13px", top: "50%", transform: "translateY(-50%)", color: SCT.na, fontSize: 18 }} />
        <Box
          component="input"
          placeholder="Search classmates…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: "100%", height: 44, pl: "38px", pr: "14px", bgcolor: "var(--color-surface-canvas-light)", border: `1.5px solid ${SCT.border}`, borderRadius: "11px", fontFamily: "inherit", fontSize: 14, outline: "none", "&:focus": { borderColor: SCT.primary } }}
        />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "10px", mb: "20px" }}>
        {classmates.length === 0 && (
          <Typography data-testid="no-classmates" sx={{ gridColumn: "1 / -1", fontSize: 14, color: SCT.na }}>
            No classmates enrolled yet — practise solo
          </Typography>
        )}
        {peers.map((p) => {
          const isSel = selected === p.id;
          return (
            <Box
              key={p.id}
              component="button"
              onClick={() => setSelected(p.id)}
              sx={{
                display: "flex", alignItems: "center", gap: "12px", p: "13px 14px", bgcolor: isSel ? "color-mix(in srgb, var(--color-primary) 6%, transparent)" : "var(--color-surface-canvas-light)",
                border: `1.5px solid ${isSel ? SCT.primary : SCT.border}`, borderRadius: "12px", cursor: "pointer",
                fontFamily: "inherit", textAlign: "left", transition: "all .12s",
              }}
            >
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: SCT.primary, color: "var(--color-on-primary)", display: "grid", placeItems: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                {initials(p.name)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: SCT.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</Typography>
              </Box>
              {isSel && <CheckIcon sx={{ fontSize: 18, color: SCT.primary }} />}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", p: "14px 18px", bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${SCT.border}`, borderRadius: "12px" }}>
        <Typography sx={{ fontSize: 12.5, color: SCT.na, fontWeight: 600 }}>
          {selected ? `Evaluator: ${peers.find((p) => p.id === selected)?.name}` : "Select a classmate to continue"}
        </Typography>
        <Box
          component="button"
          disabled={!selected}
          onClick={() => selected && onConfirm(selected)}
          sx={{ height: 40, px: "18px", borderRadius: "10px", border: "none", bgcolor: SCT.primary, color: "var(--color-on-primary)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: selected ? "pointer" : "not-allowed", opacity: selected ? 1 : 0.5 }}
        >
          Start Practice Evaluation
        </Box>
      </Box>
    </Box>
  );
}

// ── Peer practice: live assessment ───────────────────────────────────────────────
function PracticeAssessment({ checklist, peerName, onCancel, onSave, saving }) {
  const theme = useTheme();
  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <ChecklistAssessmentScreen
        checklist={checklist}
        badgeLabel="PRACTICE"
        badgeColor={theme.palette.warning.main}
        autoSubmitOnTimeout
        initialMeta={{ evaluatorName: peerName, summaryNote: "" }}
        onSave={onSave}
        headerRight={() => (
          <Box sx={{ border: "1px solid var(--color-hairline-violet)", borderRadius: "12px", p: "6px 14px" }}>
            <Typography sx={{ fontSize: 10.5, color: "var(--color-on-dark-muted) !important", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".12em" }}>Evaluator</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{peerName}</Typography>
          </Box>
        )}
        footer={(ctx) => (
          <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${SCT.border}`, borderRadius: "14px", p: "18px 20px", mb: "16px" }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: SCT.ink, mb: "14px" }}>Practice notes &amp; timing</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(31,22,51,0.7)", mb: "6px" }}>Practice date</Typography>
                <Box component="input" type="date" value={ctx.examDate} onChange={(e) => ctx.setExamDate(e.target.value)}
                  sx={{ width: "100%", height: 42, px: "12px", border: `1px solid ${SCT.border}`, borderRadius: "9px", fontFamily: "inherit", fontSize: 13.5 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(31,22,51,0.7)", mb: "6px" }}>Start time</Typography>
                <Box component="input" type="time" value={ctx.startTime} onChange={(e) => ctx.setStartTime(e.target.value)}
                  sx={{ width: "100%", height: 42, px: "12px", border: `1px solid ${SCT.border}`, borderRadius: "9px", fontFamily: "inherit", fontSize: 13.5 }} />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(31,22,51,0.7)", mb: "6px" }}>Overall note for this attempt <Box component="span" sx={{ color: SCT.na, fontWeight: 400 }}>(optional)</Box></Typography>
                <Box component="textarea" value={ctx.meta.summaryNote || ""} onChange={(e) => ctx.setMeta({ summaryNote: e.target.value })}
                  placeholder="e.g. Ran through the whole sequence twice — much smoother the second time."
                  sx={{ width: "100%", minHeight: 64, p: "10px 12px", border: `1px solid ${SCT.border}`, borderRadius: "9px", fontFamily: "inherit", fontSize: 13.5, resize: "vertical" }} />
              </Box>
            </Box>
          </Box>
        )}
        bottomBar={(ctx) => {
          const { r, canSaveDefault, save } = ctx;
          return (
            <Box sx={{ position: "sticky", bottom: 0, bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${SCT.border}`, borderRadius: "14px", p: "14px 20px", display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap", boxShadow: "0 -4px 12px -8px rgba(20,24,40,.10)" }}>
              <Box sx={{ flex: 1, display: "flex", gap: "18px" }}>
                <Box><Typography sx={{ fontSize: 10.5, color: SCT.na, fontWeight: 700, textTransform: "uppercase" }}>Scored</Typography><Typography sx={{ fontSize: 14, fontWeight: 700, color: SCT.ink }}>{r.scoredCount}/{checklist.items?.length || 0}</Typography></Box>
                <Box><Typography sx={{ fontSize: 10.5, color: SCT.na, fontWeight: 700, textTransform: "uppercase" }}>Points</Typography><Typography sx={{ fontSize: 14, fontWeight: 700, color: SCT.ink }}>{r.earned}/{r.total}</Typography></Box>
                <Box>
                  <Typography sx={{ fontSize: 10.5, color: SCT.na, fontWeight: 700, textTransform: "uppercase" }}>Result</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: r.status === "fail" ? SCT.missed : r.status === "pass" ? SCT.performed : SCT.na }}>
                    {r.status === "fail" ? `Fail · ${Math.round((r.earned / (r.total || 1)) * 100)}%` : r.status === "pass" ? `Pass · ${Math.round((r.earned / (r.total || 1)) * 100)}%` : `${Math.round((r.earned / (r.total || 1)) * 100)}% so far`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: "10px" }}>
                <Box component="button" onClick={onCancel} sx={{ height: 40, px: "16px", borderRadius: "10px", border: `1px solid ${SCT.border}`, bgcolor: "var(--color-surface-canvas-light)", color: "rgba(31,22,51,0.7)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                  Discard
                </Box>
                <Box component="button" disabled={!canSaveDefault || saving} onClick={save}
                  sx={{ height: 40, px: "18px", borderRadius: "10px", border: "none", bgcolor: SCT.primary, color: "var(--color-on-primary)", fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, cursor: canSaveDefault ? "pointer" : "not-allowed", opacity: canSaveDefault ? 1 : 0.5 }}>
                  {saving ? "Saving…" : "Save practice results"}
                </Box>
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
}

// ── Test card ──────────────────────────────────────────────────────────────────
export function TestCard({ test, submission, classId, firstSessionId, navigate }) {
  const hasCompleted = submission?.latestSubmission?.submittedAt != null;
  const submissionCount = submission?.submissionCount || 0;
  const effectiveMax = Math.max(1, test.maxRetakes || 1);
  const canTake =
    submission?.canRetake != null
      ? submission.canRetake
      : submissionCount === 0 || submissionCount < effectiveMax;
  const lastScore = submission?.latestSubmission?.gradePercent;
  const passThreshold = test.passingScore;
  const didPass =
    hasCompleted &&
    lastScore != null &&
    passThreshold != null &&
    lastScore >= passThreshold;

  const iconBg = didPass ? T.green50 : T.purple50;
  const iconColor = didPass ? T.green : T.purple;

  // Class-level tests have no session of their own; the take/results routes only need a placeholder segment.
  // ponytail: "0" until a session-less /classes/:c/tests/:id route exists.
  const sessionId = test.sessionId || firstSessionId || 0;

  const now = Date.now();
  const opensAt = test.opensAt ? new Date(test.opensAt) : null;
  const closesAt = test.closesAt ? new Date(test.closesAt) : null;
  let blocked = null;
  if (test.locked) blocked = "Locked";
  else if (opensAt && opensAt.getTime() > now)
    blocked = `Opens ${opensAt.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`;
  else if (closesAt && closesAt.getTime() < now) blocked = "Closed";
  else if (!canTake && submissionCount > 0) blocked = "No attempts left";

  return (
    <Box
      data-testid={`test-card-${test.id}`}
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "14px",
        mx: "16px",
        my: "8px",
        p: "16px 20px",
        display: "grid",
        gridTemplateColumns: { xs: "52px 1fr", md: "52px 1fr auto" },
        gap: "12px 18px",
        alignItems: "center",
        transition: "border-color .15s, box-shadow .15s",
        "&:hover": {
          borderColor: "var(--color-hairline-cool)",
          boxShadow: "var(--shadow-card-sm)",
        },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "12px",
          bgcolor: iconBg,
          color: iconColor,
          display: "grid",
          placeItems: "center",
          position: "relative",
        }}
      >
        <AssignmentOutlinedIcon sx={{ fontSize: 24 }} />
        {didPass && (
          <Box
            sx={{
              position: "absolute",
              right: -4,
              bottom: -4,
              width: 22,
              height: 22,
              borderRadius: "50%",
              bgcolor: T.green,
              color: "var(--color-on-primary)",
              display: "grid",
              placeItems: "center",
              border: "2px solid var(--color-on-primary)",
            }}
          >
            <CheckIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            color: T.ink,
            mb: "4px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {test.title}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {test.timeLimitMinutes && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                height: 24,
                px: "9px",
                borderRadius: "6px",
                bgcolor: T.surface3,
                color: T.ink2,
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 12, color: T.ink3 }} />
              {test.timeLimitMinutes} min
            </Box>
          )}
          {hasCompleted && lastScore != null && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                height: 24,
                px: "9px",
                borderRadius: "6px",
                bgcolor: T.green50,
                color: T.green,
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              <CheckIcon sx={{ fontSize: 12 }} />
              Score: {lastScore}%
            </Box>
          )}
          {submissionCount > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                height: 24,
                px: "9px",
                borderRadius: "6px",
                bgcolor: T.blue50,
                color: T.blue,
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              Attempt {submissionCount} of {effectiveMax}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          gridColumn: { xs: "1 / -1", md: "auto" },
        }}
      >
        {hasCompleted && (
          <Btn
            variant="outline"
            size="sm"
            onClick={() =>
              navigate(
                sessionId
                  ? `/classes/${classId}/session/${sessionId}/test/${test.id}/results`
                  : `/classes/${classId}/tests/${test.id}/results`,
              )
            }
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
            View results
          </Btn>
        )}
        {(
          <Btn
            variant="primary"
            size="sm"
            disabled={!canTake || Boolean(blocked)}
            onClick={() =>
              canTake && !blocked &&
              navigate(
                sessionId
                  ? `/classes/${classId}/session/${sessionId}/test/${test.id}`
                  : `/classes/${classId}/tests/${test.id}`,
              )
            }
          >
            {blocked ? (
              blocked
            ) : hasCompleted ? (
              <>
                <ReplayIcon sx={{ fontSize: 14 }} />
                Retake
              </>
            ) : (
              <>
                <PlayArrowIcon sx={{ fontSize: 14 }} />
                Take test
              </>
            )}
          </Btn>
        )}
      </Box>
    </Box>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function StudentCourseDashboardPage() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useTenant();

  const [course, setCourse] = React.useState(null);
  const [sessions, setSessions] = React.useState([]);
  const [documents, setDocuments] = React.useState([]);
  const [checklists, setChecklists] = React.useState([]);
  const [bankAssignments, setBankAssignments] = React.useState([]);
  const [tests, setTests] = React.useState([]);
  const [evalResults, setEvalResults] = React.useState({});
  const [testSubmissions, setTestSubmissions] = React.useState({});
  const [instructorName, setInstructorName] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [attendanceRecords, setAttendanceRecords] = React.useState([]);
  const [sessionDocs, setSessionDocs] = React.useState({}); // { [sessionId]: ContentItem[] }
  const [docSessionFilter, setDocSessionFilter] = React.useState("all");
  const [skillSessionFilter, setSkillSessionFilter] = React.useState("all");
  const [testSessionFilter, setTestSessionFilter] = React.useState("all");
  const [roster, setRoster] = React.useState([]);
  const [rosterSearch, setRosterSearch] = React.useState("");
  const [sessionsView, setSessionsView] = React.useState("list"); // "list" | "calendar"
  const [announcementPreview, setAnnouncementPreview] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerInitialThreadId, setDrawerInitialThreadId] =
    React.useState(null);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [learningPaths, setLearningPaths] = React.useState([]);
  const [learningPlanLoading, setLearningPlanLoading] = React.useState(false);
  const [activeDocViewer, setActiveDocViewer] = React.useState(null); // { item, planId }

  // ── Peer practice ──
  const [practiceView,      setPracticeView]      = React.useState("list"); // "list" | "preview" | "picker" | "assessment"
  const [activeChecklist,   setActiveChecklist]   = React.useState(null);
  const [myPracticeAttempts, setMyPracticeAttempts] = React.useState({}); // { [checklistId]: attempt[] }
  const [selectedPeerId,    setSelectedPeerId]    = React.useState(null);

  const loadPracticeAttempts = React.useCallback((checklistId) => {
    fetchPracticeAttempts(checklistId)
      .then((all) => {
        const mine = (all || []).filter((a) => String(a.candidateId) === String(user?.id));
        setMyPracticeAttempts((prev) => ({ ...prev, [checklistId]: mine }));
      })
      .catch(() => {});
  }, [user?.id]);

  const [peers, setPeers] = React.useState([]);
  const openPeerPicker = () => {
    setPracticeView("picker");
    fetchCourseRoster(id, { peersOnly: true }).then((r) => setPeers(r || [])).catch(() => setPeers([]));
  };

  const openPractice = (checklist) => {
    setActiveChecklist(checklist);
    setPracticeView("preview");
    loadPracticeAttempts(checklist.id);
  };

  const pathSeg = location.pathname.split("/").pop();
  const tabIdx =
    pathSeg === id
      ? 0
      : TAB_ROUTES.indexOf(pathSeg) >= 1
        ? TAB_ROUTES.indexOf(pathSeg)
        : 0;

  React.useEffect(() => {
    if (!id || !user?.id) return;
    setLoading(true);

    Promise.allSettled([
      fetchClassById(id),
      fetchSessionsForCourse(id),
      fetchClassLevelContent(id),
      fetchChecklistsForCourse(id),
      fetchTestsForCourse(id),
      fetchStudentAttendance(id, user.id),
      fetchCourseRoster(id),
      fetchClassAnnouncementPreview(id),
      fetchCourseSkillSheetAssignments(id),
    ]).then(
      ([
        courseRes,
        sessionsRes,
        docsRes,
        checklistsRes,
        testsRes,
        attendanceRes,
        rosterRes,
        announcementRes,
        bankAssignmentsRes,
      ]) => {
        const cls = courseRes.status === "fulfilled" ? courseRes.value : null;
        setCourse(cls);

        if (cls?.instructorId) {
          fetchUsersByIds([cls.instructorId])
            .then((users) => {
              if (users.length > 0)
                setInstructorName(
                  users[0].name || users[0].fullName || users[0].username,
                );
            })
            .catch(() => {});
        }

        const sessionList =
          sessionsRes.status === "fulfilled" ? sessionsRes.value || [] : [];
        setSessions(sessionList);

        const attendanceList =
          attendanceRes.status === "fulfilled" &&
          Array.isArray(attendanceRes.value)
            ? attendanceRes.value
            : [];
        setAttendanceRecords(attendanceList);

        if (sessionList.length > 0) {
          Promise.allSettled(
            sessionList.map((s) =>
              fetchSessionContent(s.id)
                .then((items) => ({ sessionId: s.id, items: items || [] }))
                .catch(() => ({ sessionId: s.id, items: [] })),
            ),
          ).then((results) => {
            const map = {};
            results.forEach((r) => {
              if (r.status === "fulfilled")
                map[r.value.sessionId] = r.value.items;
            });
            setSessionDocs(map);
          });
        }

        const docs = docsRes.status === "fulfilled" ? docsRes.value || [] : [];
        setDocuments(tagSessionLock(docs, sessionList));

        setRoster(
          rosterRes.status === "fulfilled" ? rosterRes.value || [] : [],
        );

        setAnnouncementPreview(
          announcementRes.status === "fulfilled" ? announcementRes.value : null,
        );

        const bankAssigns = bankAssignmentsRes.status === "fulfilled" ? (bankAssignmentsRes.value || []) : [];
        setBankAssignments(
          tagSessionLock(bankAssigns, sessionList).filter((a) => a.enabled !== false),
        );

        // Class-level checklists/tests come from the course-wide endpoints above.
        // Session-scoped ones live behind a separate per-session endpoint, so fetch
        // those for every session and merge before tagging lock state.
        const classLevelChecklists =
          checklistsRes.status === "fulfilled" ? checklistsRes.value || [] : [];
        const classLevelTests =
          testsRes.status === "fulfilled" ? testsRes.value || [] : [];

        Promise.all([
          Promise.allSettled(
            sessionList.map((s) =>
              fetchChecklistsForSession(id, s.id).catch(() => []),
            ),
          ),
          Promise.allSettled(
            sessionList.map((s) =>
              fetchTestsForSession(id, s.id).catch(() => []),
            ),
          ),
        ]).then(([checklistResults, testResults]) => {
          const sessionChecklists = checklistResults.flatMap((r) =>
            r.status === "fulfilled" ? r.value || [] : [],
          );
          const sessionTests = testResults.flatMap((r) =>
            r.status === "fulfilled" ? r.value || [] : [],
          );

          const cls_checklists = tagSessionLock(
            [...classLevelChecklists, ...sessionChecklists],
            sessionList,
          );
          setChecklists(cls_checklists);

          const enabledTests = tagSessionLock(
            [...classLevelTests, ...sessionTests],
            sessionList,
          ).filter((t) => t.enabled);
          setTests(enabledTests);

          const evalPromises = cls_checklists.map((cl) =>
            fetchChecklistEvaluation(id, cl.id, user.id)
              .then((ev) => ({ checklistId: cl.id, evaluation: ev }))
              .catch(() => ({ checklistId: cl.id, evaluation: null })),
          );

          Promise.allSettled(
            cls_checklists.map((cl) =>
              fetchPracticeAttempts(cl.id)
                .then((all) => ({ checklistId: cl.id, mine: (all || []).filter((a) => String(a.candidateId) === String(user.id)) }))
                .catch(() => ({ checklistId: cl.id, mine: [] }))
            )
          ).then((results) => {
            const map = {};
            results.forEach((r) => {
              if (r.status === "fulfilled") map[r.value.checklistId] = r.value.mine;
            });
            setMyPracticeAttempts(map);
          });

          const submissionPromises = enabledTests.map((t) =>
            fetchLatestTestSubmission(t.id, user.id)
              .then((s) => ({
                testId: t.id,
                canRetake: s?.canRetake ?? null,
                submissionCount:
                  s?.submissionCount ??
                  s?.submission?.submissionCount ??
                  (s?.submission ? 1 : 0),
                latestSubmission: s?.submission || null,
              }))
              .catch(() => ({
                testId: t.id,
                submissionCount: 0,
                latestSubmission: null,
                canRetake: null,
              })),
          );

          Promise.allSettled([...evalPromises, ...submissionPromises]).then(
            (results) => {
              const evals = {};
              const subs = {};
              results.forEach((r) => {
                if (r.status !== "fulfilled") return;
                if (r.value.checklistId != null)
                  evals[r.value.checklistId] = r.value.evaluation;
                if (r.value.testId != null) subs[r.value.testId] = r.value;
              });
              setEvalResults(evals);
              setTestSubmissions(subs);
              setLoading(false);
            },
          );
        });
      },
    );
  }, [id, user?.id]);

  function refreshUnreadCount() {
    if (!id || roster.length === 0) return;
    fetchMessagingThreads()
      .then((data) => {
        const { broadcast, dm } = filterThreadsForClass(data, id, roster);
        const total = [...broadcast, ...dm].reduce(
          (sum, t) => sum + (t.unread || 0),
          0,
        );
        setUnreadCount(total);
      })
      .catch((err) => console.error("Failed to fetch unread count:", err));
  }

  // Fetched once roster is available (unread badge doesn't need to block page load,
  // and filterThreadsForClass's DM-scoping heuristic depends on roster names).
  React.useEffect(() => {
    if (!id || roster.length === 0) return;
    refreshUnreadCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, roster]);

  function switchTab(idx) {
    const route = TAB_ROUTES[idx];
    navigate(route ? `/student-hub/${id}/${route}` : `/student-hub/${id}`);
  }

  const virtualLearningTabIdx = TAB_ROUTES.indexOf("virtual-learning");
  const testsTabIdx = TAB_ROUTES.indexOf("tests");
  // Fetched on mount (not gated to the tab being active) so the tab badge
  // count is correct without the student having to visit the tab first.
  React.useEffect(() => {
    if (!id) return;
    setLearningPlanLoading(true);
    fetchMyLearningPlan(id)
      .then((data) => setLearningPaths(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to fetch learning paths:", err))
      .finally(() => setLearningPlanLoading(false));
  }, [id]);

  const refreshLearningPaths = React.useCallback(() => {
    if (!id) return;
    fetchMyLearningPlan(id)
      .then((data) => setLearningPaths(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id]);

  const handleOpenLearningPlanItem = (planId, item) => {
    if (item.locked) return;
    const content =
      item.content ||
      (item.contentType === "DOCUMENT"
        ? [
            ...documents,
            ...Object.values(sessionDocs).flat(),
          ].find((document) => String(document.id) === String(item.contentRefId))
        : item.contentType === "TEST"
          ? tests.find((test) => String(test.id) === String(item.contentRefId))
          : checklists.find((checklist) => String(checklist.id) === String(item.contentRefId)));
    if (item.contentType === "DOCUMENT") {
      if (content?.url && !content.hasFile) {
        window.open(content.url, "_blank", "noopener,noreferrer");
        return;
      }
      setActiveDocViewer({ item: { ...item, content }, planId });
    } else if (item.contentType === "SKILL_SHEET" && content) {
      openPractice(content);
    } else if (item.contentType === "TEST" && content) {
      const sessionId = item.sessionId ?? content.sessionId ?? firstSession?.id;
      navigate(`/classes/${id}/session/${sessionId}/test/${content.id}`);
    }
  };

  async function handleMessageClassmate(classmate) {
    try {
      const thread = await createDmThread(Number(id), classmate.id);
      setDrawerInitialThreadId(String(thread.id));
      setDrawerOpen(true);
    } catch (err) {
      console.error("Failed to start conversation:", err);
    }
  }

  function handleOpenAnnouncement() {
    if (
      !getPinnedAnnouncementMessage(announcementPreview) ||
      announcementPreview?.threadId == null
    )
      return;
    navigate("/messaging", {
      state: { activeThreadId: String(announcementPreview.threadId) },
    });
  }

  const firstSession = sessions.length > 0 ? sessions[0] : null;

  // Build merged document list grouped by session
  const allDocsBySession = React.useMemo(() => {
    const groups = [];
    // Session-level docs
    sessions.forEach((s) => {
      const items = sessionDocs[s.id] || [];
      if (items.length > 0)
        groups.push({
          sessionId: String(s.id),
          sessionLabel: sessionLabel(sessions, String(s.id)),
          locked: !isStudentVisibleSession(s),
          items,
        });
    });
    // Class-level docs
    if (documents.length > 0)
      groups.push({
        sessionId: "classwide",
        sessionLabel: "Class Resources",
        locked: false,
        items: documents,
      });
    return groups;
  }, [sessions, sessionDocs, documents]);

  const checklistsBySession = React.useMemo(() => {
    const map = {};
    checklists.forEach((cl) => {
      const key = cl.sessionId ? String(cl.sessionId) : "classwide";
      if (!map[key]) map[key] = [];
      map[key].push(cl);
    });
    return map;
  }, [checklists]);

  const testsBySession = React.useMemo(() => {
    const map = {};
    tests.forEach((t) => {
      const key = t.sessionId ? String(t.sessionId) : "classwide";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [tests]);

  const gradedItems = React.useMemo(() => {
    const testRows = tests.map((test) => {
      const submission = testSubmissions[test.id];
      const latest = submission?.latestSubmission;
      const attempted = latest?.submittedAt != null;
      const scorePct = testScorePct(latest);
      const passed = attempted && testPassedLive(latest, test);
      return {
        key: `test-${test.id}`,
        title: test.title,
        type: "Test",
        attempted,
        result: attempted
          ? `${scorePct != null ? scorePct + "%" : `${latest.score}/${latest.totalPoints}`}${attempted ? (passed ? " · Passed" : " · Failed") : ""}`
          : null,
        date: attempted ? latest.submittedAt : null,
      };
    });
    const checklistRows = checklists.map((cl) => {
      const evaluation = evalResults[cl.id];
      const attempted = !!evaluation?.locked;
      return {
        key: `checklist-${cl.id}`,
        title: cl.title,
        type: "Checklist",
        attempted,
        result: attempted
          ? evaluation.finalResult === "PASS"
            ? "Passed"
            : "Failed"
          : null,
        date: attempted
          ? evaluation.examinationDate ||
            evaluation.updatedAt ||
            evaluation.createdAt ||
            null
          : null,
      };
    });
    return [...testRows, ...checklistRows].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });
  }, [tests, testSubmissions, checklists, evalResults]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ── Peer practice sub-views (replace the tabbed layout while active) ──
  if (activeChecklist && practiceView !== "list") {
    const closePractice = () => {
      setPracticeView("list");
      setActiveChecklist(null);
      setSelectedPeerId(null);
    };

    if (practiceView === "preview") {
      return (
        <Box sx={{ pb: "60px", pt: "26px", px: { xs: "16px", md: "40px" } }}>
          <SkillSheetPreview
            checklist={activeChecklist}
            myAttempts={myPracticeAttempts[activeChecklist.id]}
            onBack={closePractice}
            onStartPeerEval={openPeerPicker}
            onStartSolo={() => { setSelectedPeerId(null); setPracticeView("assessment"); }}
          />
        </Box>
      );
    }
    if (practiceView === "picker") {
      return (
        <Box sx={{ pb: "60px", pt: "26px", px: { xs: "16px", md: "40px" } }}>
          <PeerPicker
            roster={peers}
            selfId={user?.id}
            onBack={() => setPracticeView("preview")}
            onConfirm={(peerId) => { setSelectedPeerId(peerId); setPracticeView("assessment"); }}
          />
        </Box>
      );
    }
    if (practiceView === "assessment") {
      const peer = peers.find((r) => r.id === selectedPeerId);
      return (
        <Box sx={{ pb: "60px", pt: "26px", px: { xs: "16px", md: "40px" } }}>
          <PracticeAssessment
            checklist={activeChecklist}
            peerName={selectedPeerId == null ? "Solo practice" : peer?.name || "Classmate"}
            onCancel={() => setPracticeView("preview")}
            onSave={(payload) => {
              createPracticeAttempt(activeChecklist.id, selectedPeerId == null ? payload : { ...payload, evaluatorId: selectedPeerId })
                .then(() => {
                  loadPracticeAttempts(activeChecklist.id);
                  setPracticeView("preview");
                });
            }}
          />
        </Box>
      );
    }
  }

  // ── KPI computations ──
  const sessionsAttended = attendanceRecords.filter(
    (r) =>
      r.status === "PRESENT" ||
      r.status === "present" ||
      r.attended === true ||
      r.attended === 1,
  ).length;
  const skillsPassed = Object.values(evalResults).filter(
    (e) => e?.locked && e?.finalResult === "PASS",
  ).length;
  const testsPassed = Object.values(testSubmissions).filter((s) => {
    const sub = s?.latestSubmission;
    if (!sub?.gradePercent) return false;
    const test = tests.find((t) => t.id === s.testId);
    return test?.passingScore != null && sub.gradePercent >= test.passingScore;
  }).length;

  const testsPassedForBreakdown = Object.values(testSubmissions).filter((s) => {
    const sub = s?.latestSubmission;
    if (!sub) return false;
    const test = tests.find((t) => t.id === s.testId);
    return testPassedLive(sub, test);
  }).length;

  const attendancePct = pct(sessionsAttended, sessions.length);
  const testsPct = pct(testsPassedForBreakdown, tests.length);
  const skillsPct = pct(skillsPassed, checklists.length);

  const rosterQuery = rosterSearch.trim().toLowerCase();
  const filteredRoster = rosterQuery
    ? roster.filter((s) => {
        const name = (s.name || "").toLowerCase();
        const email = (s.email || "").toLowerCase();
        return name.includes(rosterQuery) || email.includes(rosterQuery);
      })
    : roster;

  const sessionsById = Object.fromEntries(
    sessions.map((s) => [String(s.id), s]),
  );
  const activeCountOpts = { sessionsById };
  const learningPathItemCount = learningPaths.reduce(
    (sum, p) => sum + (Array.isArray(p.items) ? p.items.length : 0),
    0,
  );

  const hasLiveSession = sessions.some((s) => s.enabled === true);

  const studentTabs = TABS.map((tab) => {
    const Icon = tab.icon;
    const count =
      tab.key === "sessions" ? sessions.length :
      tab.key === "documents" ? countActive(documents, activeCountOpts) + countActive(Object.values(sessionDocs).flat(), activeCountOpts) :
      tab.key === "skills" ? countActive(checklists, activeCountOpts) + countActive(bankAssignments, activeCountOpts) :
      tab.key === "tests" ? countActive(tests, activeCountOpts) :
      tab.key === "virtual-learning" ? learningPathItemCount || null :
      tab.key === "roster" ? roster.length : null;
    return {
      key: tab.key,
      icon: <Icon sx={{ fontSize: 15 }} />,
      label: tab.label,
      live: tab.key === "sessions" ? hasLiveSession : false,
      count,
    };
  });

  return (
    <Box sx={{ pb: "60px" }}>
      <ClassOverview
        breadcrumbLabel="My Classes"
        onBreadcrumbClick={() => navigate("/classes")}
        title={course?.title || "Loading…"}
        chips={[
          ...(course?.courseCode ? [{ label: course.courseCode, mono: true }] : []),
          ...(course?.category ? [{ label: course.category }] : []),
        ]}
        subtitle={instructorName || undefined}
        tabs={studentTabs}
        activeTab={TABS[tabIdx]?.key || "sessions"}
        onTabChange={(key) => switchTab(TABS.findIndex((tab) => tab.key === key))}
        actions={[{ key: "messages", label: unreadCount ? `Messages (${unreadCount})` : "Messages", variant: "outlined", onClick: () => { setDrawerInitialThreadId(null); setDrawerOpen(true); } }]}
        showContentFrame={false}
      />
      <Box
        sx={{
          maxWidth: 1240,
          mx: "auto",
          px: { xs: "16px", md: "40px" },
          pt: 0,
        }}
      >

        {/* Course header */}
        <Box sx={{ display: "none" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
              mb: "4px",
            }}
          >
            <Typography
              fontWeight={600}
              color={tPrimary}
              sx={{
                letterSpacing: "-.01em",
                fontFamily: "var(--font-display)",
                fontSize: { xs: "26px", sm: "34px" },
                lineHeight: 1.15,
              }}
            >
              {course?.title || "Loading…"}
            </Typography>
            <IconButton
              onClick={() => {
                setDrawerInitialThreadId(null);
                setDrawerOpen(true);
              }}
              sx={{
                color: "rgba(31,22,51,0.45)",
                "&:hover": { color: tPrimary, bgcolor: "color-mix(in srgb, var(--color-primary) 6%, transparent)" },
              }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Box>
          <Typography sx={{ fontSize: "15px", color: "var(--color-ink)", opacity: 0.6 }}>
            {course?.courseCode && (
              <b style={{ opacity: 1, fontWeight: 600 }}>
                {course.courseCode}
              </b>
            )}
            {course?.courseCode && course?.category && " · "}
            {course?.category && <span>{course.category}</span>}
            {instructorName && ` · ${instructorName}`}
          </Typography>
        </Box>

        {(documents.length > 0 ||
          Object.values(sessionDocs).flat().length > 0) && (
          <Typography
            variant="caption"
            sx={{ display: "none" }}
          >
            {documents.length + Object.values(sessionDocs).flat().length}{" "}
            document
            {documents.length + Object.values(sessionDocs).flat().length !== 1
              ? "s"
              : ""}{" "}
            available
          </Typography>
        )}

        {/* Announcement banner — only shown when there is a message */}
        {getPinnedAnnouncementMessage(announcementPreview) &&
          (() => {
            const announcementMessage =
              getPinnedAnnouncementMessage(announcementPreview);
            const clickable = announcementPreview?.threadId != null;
            return (
              <Box
                component={clickable ? "button" : "div"}
                onClick={clickable ? handleOpenAnnouncement : undefined}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  width: "100%",
                  textAlign: "left",
                  fontFamily: "inherit",
                  mb: 2,
                  bgcolor: "var(--color-warn-bg)",
                  borderRadius: "10px",
                  p: "12px 16px",
                  border: "2px solid var(--color-warn)",
                  boxShadow: "0 2px 10px -4px color-mix(in srgb, var(--color-warn) 35%, transparent)",
                  cursor: clickable ? "pointer" : "default",
                  "&:hover": clickable ? { borderColor: "var(--color-warn)" } : {},
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: "8px",
                    bgcolor: "var(--color-warn-bg)",
                    color: "var(--color-warn)",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CampaignOutlinedIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      mb: "2px",
                    }}
                  >
                    <Box
                      sx={{ fontSize: 12.5, fontWeight: 700, color: "var(--color-ink)" }}
                    >
                      {announcementMessage.senderName || "Announcement"}
                    </Box>
                    <Box sx={{ fontSize: 11.5, color: "rgba(31,22,51,0.45)" }}>
                      {formatRelativeTime(announcementMessage.sentAt)}
                    </Box>
                  </Box>
                  <Box sx={{ fontSize: 13, color: "rgba(31,22,51,0.6)", lineHeight: 1.4 }}>
                    {truncateText(announcementMessage.content, 140)}
                  </Box>
                </Box>
              </Box>
            );
          })()}

        {/* Tab bar */}
        <Paper
          elevation={0}
          sx={{
            display: "none",
            borderRadius: "12px 12px 0 0",
            border: `1px solid ${DS.border}`,
            borderBottom: "none",
            bgcolor: "var(--color-surface-canvas-light)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabIdx}
            onChange={(_, v) => switchTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons={false}
            sx={{
              px: { xs: 0, sm: 2 },
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                textTransform: "none",
                minHeight: 48,
                px: { xs: 1.25, sm: 2 },
              },
            }}
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const count =
                tab.key === "sessions"
                  ? sessions.length
                  : tab.key === "documents"
                    ? documents.length +
                      Object.values(sessionDocs).flat().length
                    : tab.key === "skills"
                      ? checklists.length
                      : tab.key === "tests"
                        ? tests.length
                        : tab.key === "roster"
                          ? roster.length
                          : null;
              return (
                <Tab
                  key={tab.key}
                  label={
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Icon sx={{ fontSize: 14 }} />
                      {tab.label}
                      {count !== null && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: "10px",
                            fontWeight: 700,
                            px: "5px",
                            py: "1px",
                            borderRadius: "999px",
                            bgcolor: "var(--color-surface-press-light)",
                            color: "rgba(31,22,51,0.55)",
                          }}
                        >
                          {count}
                        </Box>
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Paper>

        {/* Tab content — frameless, matching the instructor CourseHub view */}
        <Box sx={{ pt: "24px", pb: 3 }}>
          {/* ── Sessions tab ──────────────────────────────────────────────── */}
          {tabIdx === 0 && (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "14px" }}>
                <Box sx={{ display: "inline-flex", border: `1px solid ${T.line}`, borderRadius: "10px", overflow: "hidden", bgcolor: "var(--color-surface-canvas-light)" }}>
                  {[{ key: "list", label: "List", icon: ViewListIcon }, { key: "calendar", label: "Calendar", icon: ViewModuleIcon }].map(({ key, label, icon: Icon }) => (
                    <Box
                      key={key}
                      component="button"
                      onClick={() => setSessionsView(key)}
                      sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "14px", height: 34, fontSize: 12.5, fontWeight: 600, color: sessionsView === key ? "var(--color-on-primary)" : T.ink2, bgcolor: sessionsView === key ? tPrimary : "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                    >
                      <Icon sx={{ fontSize: 15 }} />
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>

              {sessionsView === "calendar" && (
                <Box sx={{ mb: sessions.length === 0 ? "16px" : 0 }}>
                  <SessionCalendarView sessions={sessions} T={T} onSelectSession={() => setSessionsView("list")} />
                </Box>
              )}

              {sessions.length === 0 ? (
                <EmptyState message="No sessions scheduled yet." />
              ) : sessionsView === "list" ? (
                <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${T.line}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)", overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", px: "20px", pt: "18px", pb: "12px" }}>
                    <CalendarMonthIcon sx={{ fontSize: 20, color: T.ink3 }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, fontFamily: "var(--font-display)" }}>
                      Sessions <Box component="span" sx={{ color: T.ink3, fontWeight: 600 }}>{sessions.length}</Box>
                    </Typography>
                  </Box>
                  <Box sx={{ mx: "20px", mb: "14px", px: "14px", py: "10px", borderRadius: "10px", bgcolor: "color-mix(in srgb, var(--color-primary) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--color-primary) 15%, transparent)", fontSize: 13, color: T.ink2, lineHeight: 1.45 }}>
                    Expand any session for topics, sign in/out, and materials. Documents unlock once that session opens.
                  </Box>
                  {sessions.map((session, idx) => {
                    const status = getSessionStatus(session);
                    const dateObj = session.sessionDate
                      ? new Date(session.sessionDate + "T00:00:00")
                      : null;
                    const mon = dateObj
                      ? dateObj
                          .toLocaleDateString("en-US", { month: "short" })
                          .toUpperCase()
                      : null;
                    const day = dateObj ? dateObj.getDate() : null;
                    const materials = materialsForSession({
                      sessionId: session.id,
                      sessionDocs,
                      documents,
                      checklists,
                      tests,
                      status,
                    });
                    return (
                      <SessionRow
                        key={session.id}
                        session={session}
                        idx={idx}
                        isLast={idx === sessions.length - 1}
                        status={status}
                        mon={mon}
                        day={day}
                        tPrimary={tPrimary}
                        materials={materials}
                        onPractice={openPractice}
                        classId={id}
                        firstSessionId={sessions[0]?.id}
                        navigate={navigate}
                        testSubmissions={testSubmissions}
                      />
                    );
                  })}
                </Box>
              ) : null}
            </Box>
          )}

          {/* ── Documents tab ─────────────────────────────────────────────── */}
          {tabIdx === 1 && (
            <Box>
              {allDocsBySession.length === 0 ? (
                <EmptyState message="No documents have been added to this course yet." />
              ) : (
                <Box
                  sx={{
                    bgcolor: "var(--color-surface-canvas-light)",
                    border: `1px solid ${T.line}`,
                    borderRadius: "16px",
                    boxShadow:
                      "var(--shadow-card-sm)",
                    overflow: "hidden",
                  }}
                >
                  {/* Session filter toolbar */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      p: "14px 20px",
                      borderBottom: `1px solid ${T.line2}`,
                      bgcolor: T.surface2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ fontSize: 12, fontWeight: 700, color: T.ink2 }}>
                      Filter by session
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="select"
                        value={docSessionFilter}
                        onChange={(e) => setDocSessionFilter(e.target.value)}
                        sx={{
                          height: 36,
                          pl: "12px",
                          pr: "32px",
                          bgcolor: "var(--color-surface-canvas-light)",
                          border: `1px solid ${T.line}`,
                          borderRadius: "9px",
                          fontFamily: "inherit",
                          fontSize: 13,
                          color: T.ink,
                          appearance: "none",
                          cursor: "pointer",
                          minWidth: 260,
                          "&:focus": {
                            outline: "none",
                            borderColor: tPrimary,
                            boxShadow: `0 0 0 3px ${T.p10}`,
                          },
                        }}
                      >
                        <option value="all">All Sessions</option>
                        {sessions.map((s) => (
                          <option key={s.id} value={String(s.id)}>
                            {sessionLabel(sessions, String(s.id))}
                          </option>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          right: 11,
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: T.ink4,
                        }}
                      >
                        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Session groups */}
                  {allDocsBySession
                    .filter(
                      (g) =>
                        docSessionFilter === "all" ||
                        g.sessionId === docSessionFilter ||
                        g.sessionId === "classwide",
                    )
                    .map((group, gi, arr) => (
                      <Box
                        key={group.sessionId}
                        sx={{
                          p: "16px 20px",
                          borderBottom:
                            gi < arr.length - 1
                              ? `1px solid ${T.line2}`
                              : "none",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.ink3,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                            mb: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {group.sessionLabel}
                          {group.locked && (
                            <LockIcon sx={{ fontSize: 13, color: T.ink4 }} />
                          )}
                        </Box>
                        <LockOverlay locked={group.locked}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            {group.items.map((doc) => (
                              <DocCard key={doc.id} item={doc} />
                            ))}
                          </Box>
                        </LockOverlay>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Skill Sheets tab ──────────────────────────────────────────── */}
          {tabIdx === 2 && (
            <Box>
              {bankAssignments.length > 0 && (
                <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${T.line}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)", overflow: "hidden", mb: 2 }}>
                  <Box sx={{ px: "20px", pt: "16px", pb: "8px", fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".06em" }}>
                    Skill Sheets For This Course
                  </Box>
                  {Object.entries(
                    bankAssignments.reduce((acc, a) => {
                      const cat = a.sheet?.category || "Uncategorized";
                      (acc[cat] = acc[cat] || []).push(a);
                      return acc;
                    }, {})
                  ).map(([category, items], gi, arr) => (
                    <Box key={category} sx={{ borderBottom: gi < arr.length - 1 ? `1px solid ${T.line2}` : "none", px: "20px", pb: "14px" }}>
                      <Box sx={{ fontSize: 11.5, fontWeight: 700, color: T.ink4, textTransform: "uppercase", letterSpacing: ".05em", mb: "8px" }}>
                        {category}
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {items.map((a) => (
                          <LockOverlay key={a.id} locked={a._sessionLocked}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "12px 14px", borderRadius: "12px", border: `1px solid ${T.line}`, bgcolor: T.surface2 }}>
                              <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18, color: T.teal }} />
                              <Box sx={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{a.sheet?.title || "Untitled Skill Sheet"}</Box>
                            </Box>
                          </LockOverlay>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {checklists.length === 0 ? (
                bankAssignments.length === 0 && (
                  <EmptyState message="No skill check sheets have been added to this course yet." />
                )
              ) : (
                <Box sx={{ bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${T.line}`, borderRadius: "16px", boxShadow: "var(--shadow-card-sm)", overflow: "hidden" }}>
                  {/* Filter */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "14px 20px", borderBottom: `1px solid ${T.line2}`, bgcolor: T.surface2, flexWrap: "wrap" }}>
                    <Box sx={{ fontSize: 12, fontWeight: 700, color: T.ink2 }}>Filter by session</Box>
                    <Box sx={{ position: "relative" }}>
                      <Box component="select" value={skillSessionFilter} onChange={(e) => setSkillSessionFilter(e.target.value)}
                        sx={{ height: 36, pl: "12px", pr: "32px", bgcolor: "var(--color-surface-canvas-light)", border: `1px solid ${T.line}`, borderRadius: "9px", fontFamily: "inherit", fontSize: 13, color: T.ink, appearance: "none", cursor: "pointer", minWidth: 260, "&:focus": { outline: "none", borderColor: T.primary } }}>
                        <option value="all">All Sessions</option>
                        {sessions.map((s) => <option key={s.id} value={String(s.id)}>{sessionLabel(sessions, String(s.id))}</option>)}
                      </Box>
                      <Box sx={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.ink4 }}>
                        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Grouped items */}
                  {Object.entries(checklistsBySession)
                    .filter(([sid]) => skillSessionFilter === "all" || sid === skillSessionFilter || sid === "classwide")
                    .map(([sid, cls], gi, arr) => (
                      <Box
                        key={sid}
                        sx={{
                          borderBottom:
                            gi < arr.length - 1
                              ? `1px solid ${T.line2}`
                              : "none",
                        }}
                      >
                        <Box
                          sx={{
                            px: "20px",
                            pt: "16px",
                            pb: "8px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.ink3,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                          }}
                        >
                          {sessionLabel(sessions, sid)}
                        </Box>
                        {cls.map((cl) => (
                          <LockOverlay key={cl.id} locked={cl._sessionLocked}>
                            <StudentSkillCard
                              checklist={cl}
                              evaluation={evalResults[cl.id]}
                              myAttempts={myPracticeAttempts[cl.id]}
                              onPractice={openPractice}
                            />
                          </LockOverlay>
                        ))}
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Tests tab ─────────────────────────────────────────────────── */}
          {tabIdx === testsTabIdx && (
            <Box>
              {tests.length === 0 ? (
                <EmptyState message="No tests are available for this course yet." />
              ) : (
                <Box
                  sx={{
                    bgcolor: "var(--color-surface-canvas-light)",
                    border: `1px solid ${T.line}`,
                    borderRadius: "16px",
                    boxShadow:
                      "var(--shadow-card-sm)",
                    overflow: "hidden",
                  }}
                >
                  {/* Filter */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      p: "14px 20px",
                      borderBottom: `1px solid ${T.line2}`,
                      bgcolor: T.surface2,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ fontSize: 12, fontWeight: 700, color: T.ink2 }}>
                      Filter by session
                    </Box>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        component="select"
                        value={testSessionFilter}
                        onChange={(e) => setTestSessionFilter(e.target.value)}
                        sx={{
                          height: 36,
                          pl: "12px",
                          pr: "32px",
                          bgcolor: "var(--color-surface-canvas-light)",
                          border: `1px solid ${T.line}`,
                          borderRadius: "9px",
                          fontFamily: "inherit",
                          fontSize: 13,
                          color: T.ink,
                          appearance: "none",
                          cursor: "pointer",
                          minWidth: 260,
                          "&:focus": { outline: "none", borderColor: tPrimary },
                        }}
                      >
                        <option value="all">All Sessions</option>
                        {sessions.map((s) => (
                          <option key={s.id} value={String(s.id)}>
                          {sessionLabel(sessions, String(s.id))}
                          </option>
                        ))}
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          right: 11,
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: T.ink4,
                        }}
                      >
                        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                  </Box>

                  {/* Grouped items */}
                  {Object.entries(testsBySession)
                    .filter(
                      ([sid]) =>
                        testSessionFilter === "all" ||
                        sid === testSessionFilter ||
                        sid === "classwide",
                    )
                    .map(([sid, tsts], gi, arr) => (
                      <Box
                        key={sid}
                        sx={{
                          borderBottom:
                            gi < arr.length - 1
                              ? `1px solid ${T.line2}`
                              : "none",
                        }}
                      >
                        <Box
                          sx={{
                            px: "20px",
                            pt: "16px",
                            pb: "8px",
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.ink3,
                            textTransform: "uppercase",
                            letterSpacing: ".06em",
                          }}
                        >
                          {sessionLabel(sessions, sid)}
                        </Box>
                        {tsts.map((test) => (
                          <LockOverlay key={test.id} locked={test._sessionLocked}>
                            <TestCard
                              test={test}
                              submission={testSubmissions[test.id]}
                              classId={id}
                              firstSessionId={firstSession?.id}
                              navigate={navigate}
                            />
                          </LockOverlay>
                        ))}
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Reports tab ───────────────────────────────────────────────── */}
          {tabIdx === 5 && (
            <Box>
              {/* Stat row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3,1fr)" },
                  gap: "12px",
                  mb: "16px",
                }}
              >
                {[
                  {
                    val: `${sessionsAttended} / ${sessions.length}`,
                    label: "Sessions Attended",
                  },
                  {
                    val: `${skillsPassed} / ${checklists.length}`,
                    label: "Skills Passed",
                  },
                  {
                    val: `${testsPassed} / ${tests.length}`,
                    label: "Tests Passed",
                  },
                ].map(({ val, label }) => (
                  <Box
                    key={label}
                    sx={{
                      bgcolor: "var(--color-surface-canvas-light)",
                      border: `1px solid ${T.line}`,
                      borderRadius: "14px",
                      p: "16px 18px",
                      boxShadow:
                        "var(--shadow-card-sm)",
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 26,
                        fontWeight: 800,
                        letterSpacing: "-.03em",
                        color: T.ink,
                      }}
                    >
                      {val}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: T.ink3,
                        mt: "4px",
                      }}
                    >
                      {label}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Progress detail panel */}
              <Box
                sx={{
                  bgcolor: "var(--color-surface-canvas-light)",
                  border: `1px solid ${T.line}`,
                  borderRadius: "16px",
                  boxShadow:
                    "var(--shadow-card-sm)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    p: "15px 20px 13px",
                    borderBottom: `1px solid ${T.line2}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: T.p10,
                      color: tPrimary,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    Your Progress
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: "18px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {[
                    {
                      label: "Sessions attended",
                      val: `${sessionsAttended} / ${sessions.length}`,
                    },
                    {
                      label: "Skill checklists passed",
                      val: `${skillsPassed} / ${checklists.length}`,
                    },
                    {
                      label: "Tests passed",
                      val: `${testsPassed} / ${tests.length}`,
                    },
                    {
                      label: "Documents available",
                      val: String(
                        documents.length +
                          Object.values(sessionDocs).flat().length,
                      ),
                    },
                  ].map(({ label, val }) => (
                    <Box
                      key={label}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13.5,
                      }}
                    >
                      <Box sx={{ color: T.ink3 }}>{label}</Box>
                      <Box sx={{ fontWeight: 700, color: T.ink }}>{val}</Box>
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        )}

              <Box
                sx={{
                  bgcolor: "var(--color-surface-canvas-light)",
                  border: `1px solid ${T.line}`,
                  borderRadius: "16px",
                  boxShadow:
                    "var(--shadow-card-sm)",
                  overflow: "hidden",
                  mt: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    p: "15px 20px 13px",
                    borderBottom: `1px solid ${T.line2}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: T.p10,
                      color: tPrimary,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <BarChartIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    Grade Breakdown
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: "18px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "18px",
                  }}
                >
                  {[
                    { label: "Attendance", value: attendancePct },
                    { label: "Tests Passed", value: testsPct },
                    { label: "Skills Passed", value: skillsPct },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: "6px",
                          fontSize: 13,
                        }}
                      >
                        <Box sx={{ color: T.ink2, fontWeight: 600 }}>
                          {label}
                        </Box>
                        <Box
                          sx={{
                            fontWeight: 700,
                            color: value === null ? T.ink4 : T.ink,
                          }}
                        >
                          {value === null ? "N/A" : `${value}%`}
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={value ?? 0}
                        sx={{
                          height: 8,
                          borderRadius: "999px",
                          bgcolor: T.surface3,
                          "& .MuiLinearProgress-bar": {
                            borderRadius: "999px",
                            bgcolor: value === null ? T.ink4 : tPrimary,
                          },
                          opacity: value === null ? 0.4 : 1,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Breakdown table */}
              <Box
                sx={{
                  bgcolor: "var(--color-surface-canvas-light)",
                  border: `1px solid ${T.line}`,
                  borderRadius: "16px",
                  boxShadow:
                    "var(--shadow-card-sm)",
                  overflow: "hidden",
                  mt: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    p: "15px 20px 13px",
                    borderBottom: `1px solid ${T.line2}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: T.p10,
                      color: tPrimary,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    Graded Items
                  </Typography>
                </Box>
                {gradedItems.length === 0 ? (
                  <Box sx={{ p: "24px 20px" }}>
                    <Typography sx={{ fontSize: 13.5, color: T.ink3 }}>
                      No tests or skill checklists are configured for this
                      course yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto auto",
                      gap: "0",
                    }}
                  >
                    {gradedItems.map((item, idx) => (
                      <Box key={item.key} sx={{ display: "contents" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: "12px 20px",
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: item.attempted ? T.ink : T.ink4,
                            borderBottom:
                              idx < gradedItems.length - 1
                                ? `1px solid ${T.line2}`
                                : "none",
                          }}
                        >
                          {item.title}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: "12px 12px",
                            fontSize: 12,
                            fontWeight: 700,
                            color: item.attempted ? T.ink3 : T.ink4,
                            borderBottom:
                              idx < gradedItems.length - 1
                                ? `1px solid ${T.line2}`
                                : "none",
                          }}
                        >
                          {item.type}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: "12px 12px",
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: item.attempted ? T.ink2 : T.ink4,
                            borderBottom:
                              idx < gradedItems.length - 1
                                ? `1px solid ${T.line2}`
                                : "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.attempted ? item.result : "Not attempted"}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: "12px 20px 12px 12px",
                            fontSize: 12,
                            fontWeight: 500,
                            color: T.ink3,
                            borderBottom:
                              idx < gradedItems.length - 1
                                ? `1px solid ${T.line2}`
                                : "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.date
                            ? formatDate(String(item.date).slice(0, 10))
                            : "—"}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ── Roster tab ───────────────────────────────────────────────── */}
          {tabIdx === 6 && (
            <Box>
              {roster.length === 0 ? (
                <EmptyState message="No classmates found in this course yet." />
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: "#ffffff",
                    border: `1px solid ${T.line}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                  }}
                >
                  {/* Toolbar — mirrors instructor Class Roster (no status filters) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: "18px",
                      py: "14px",
                      borderBottom: `1px solid ${T.line2}`,
                      bgcolor: "#ffffff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: T.ink,
                        letterSpacing: "-.01em",
                        display: "inline-flex",
                        alignItems: "baseline",
                        gap: 1,
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      Class Roster
                      <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink3 }}>
                        · {roster.length} {roster.length === 1 ? "member" : "members"}
                      </Box>
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "#ffffff",
                        border: `1px solid ${T.line}`,
                        borderRadius: "9px",
                        px: 1.5,
                        height: 34,
                        color: T.ink3,
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 220 },
                        maxWidth: 320,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 14 }} />
                      <InputBase
                        placeholder="Search by name or email…"
                        value={rosterSearch}
                        onChange={(e) => setRosterSearch(e.target.value)}
                        sx={{ flex: 1, fontSize: 13, color: T.ink }}
                      />
                    </Box>
                  </Box>

                  {/* Column headers */}
                  <Box
                    sx={{
                      display: { xs: "none", sm: "grid" },
                      gridTemplateColumns: STUDENT_ROSTER_GRID,
                      gap: "14px",
                      alignItems: "center",
                      px: "18px",
                      py: "10px",
                      borderBottom: `1px solid ${T.line2}`,
                      bgcolor: "#ffffff",
                    }}
                  >
                    {["Student", "Role", "Actions"].map((label) => (
                      <Typography
                        key={label}
                        sx={{
                          fontSize: "10.5px",
                          fontWeight: 700,
                          letterSpacing: ".08em",
                          textTransform: "uppercase",
                          color: T.ink3,
                          textAlign: label === "Actions" ? "right" : "left",
                        }}
                      >
                        {label}
                      </Typography>
                    ))}
                  </Box>

                  {filteredRoster.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Typography sx={{ fontSize: 14, color: T.ink3 }}>
                        No classmates match your search.
                      </Typography>
                    </Box>
                  ) : (
                    filteredRoster.map((student) => (
                      <RosterMemberCard
                        key={student.id}
                        student={student}
                        tPrimary={tPrimary}
                        onMessage={handleMessageClassmate}
                      />
                    ))
                  )}
                </Paper>
              )}
            </Box>
          )}

          {/* ── Virtual Learning tab ─────────────────────────────────────── */}
          {tabIdx === virtualLearningTabIdx && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {learningPlanLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : !learningPaths.length ? (
                <EmptyState message="No learning paths have been set up for this course yet." />
              ) : (
                learningPaths.map((path) => (
                  <Box key={path.id}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
                          {path.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: T.ink4, display: "flex", alignItems: "center", gap: "4px" }}>
                          {path.sessionId == null ? "Global" : sessionLabel(sessions, String(path.sessionId))}
                          {path.sessionActive === false && (
                            <>
                              <LockIcon sx={{ fontSize: 12 }} />
                              Locked until session opens
                            </>
                          )}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: T.ink3, fontWeight: 600 }}>
                        {path.completionPercent ?? 0}% complete
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={path.completionPercent ?? 0}
                      sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
                    />
                    {!path.items?.length ? (
                      <EmptyState message="No items in this path yet." />
                    ) : (
                      <Box
                        sx={{
                          bgcolor: "var(--color-surface-canvas-light)",
                          border: `1px solid ${T.line}`,
                          borderRadius: "16px",
                          boxShadow: "var(--shadow-card-sm)",
                          overflow: "hidden",
                        }}
                      >
                        {path.items
                          .slice()
                          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                          .map((item, idx, arr) => {
                            const waitingOnInstructor =
                              item.locked && path.pacingMode === "INSTRUCTOR_PACED";
                            const statusLabel = item.completed
                              ? "Completed"
                              : item.locked
                                ? waitingOnInstructor
                                  ? "Waiting for instructor"
                                  : "Locked"
                                : "Available";
                            const statusColor = item.completed
                              ? "var(--color-success)"
                              : item.locked
                                ? T.ink4
                                : tPrimary;
                            const score = item.score;
                            return (
                              <Box
                                key={item.id}
                                onClick={() => handleOpenLearningPlanItem(path.id, item)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  p: "14px 20px",
                                  borderBottom: idx < arr.length - 1 ? `1px solid ${T.line2}` : "none",
                                  cursor: item.locked ? "default" : "pointer",
                                  opacity: item.locked ? 0.6 : 1,
                                  "&:hover": item.locked ? {} : { bgcolor: T.surface },
                                }}
                              >
                                <Box sx={{ fontSize: 13, color: T.ink4, width: 20 }}>{idx + 1}</Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {item.title || "Untitled"}
                                  </Box>
                                  <Box sx={{ fontSize: 11.5, color: T.ink3, textTransform: "capitalize" }}>
                                    {(item.contentType || "").toLowerCase().replace("_", " ")}
                                  </Box>
                                </Box>
                                {score && item.contentType === "TEST" && score.score != null && (
                                  <Box sx={{ fontSize: 11.5, color: T.ink3, whiteSpace: "nowrap" }}>
                                    {score.score}/{score.totalPoints}
                                  </Box>
                                )}
                                {score && item.contentType === "SKILL_SHEET" && score.gradePercent != null && (
                                  <Box sx={{ fontSize: 11.5, color: T.ink3, whiteSpace: "nowrap" }}>
                                    {Math.round(score.gradePercent)}%
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    color: statusColor,
                                    border: `1px solid ${statusColor}`,
                                    borderRadius: "999px",
                                    px: "10px",
                                    py: "3px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {statusLabel}
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>
          )}

          <VirtualLearningDocumentViewer
            open={Boolean(activeDocViewer)}
            item={activeDocViewer?.item}
            courseId={id}
            planId={activeDocViewer?.planId}
            onClose={() => setActiveDocViewer(null)}
            onCompleted={() => refreshLearningPaths()}
          />
        </Box>
      </Box>

      <MessagingDrawer
        open={drawerOpen}
        presentation="modal"
        onClose={() => {
          setDrawerOpen(false);
          refreshUnreadCount();
        }}
        classId={id}
        roster={roster}
        currentUser={user}
        initialThreadId={drawerInitialThreadId}
        onOpenFullInbox={(threadId) =>
          navigate("/messaging", {
            state: { activeThreadId: String(threadId) },
          })
        }
        T={T}
      />
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: "14px",
        p: "48px 32px",
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontSize: 14, color: T.ink3, fontWeight: 500 }}>
        {message}
      </Typography>
    </Box>
  );
}
